/**
 * WHO IS PLAYING — one identity, every surface (§ F117; accounts § F201).
 *
 * § F201 (2026-09-30): a player is an ACCOUNT. You become one by typing the
 * code emailed to you (`/login`, `/register`), and what that hands back — the
 * player id, the name and a SESSION token — is what this module stores.
 *
 * A device can hold SEVERAL signed-in accounts (the family iPad): `gtw_accounts`
 * is every account signed in here, and `gtw_player` is the one playing right
 * now. "Change players" (`/who`) switches between the accounts in that list and
 * nobody else — it never shows a name that has not signed in on this device.
 *
 * Trivia's three `ppr_*` keys are still mirrored on every write because
 * `TrivianightPage` reads them directly (rewriting that file is not what this
 * change is for).
 *
 * Nothing here talks to the network except `signOutPlayer`, which revokes the
 * session on the server as it forgets it here.
 */

export interface StoredPlayer {
  id: string;
  name: string;
  /** The session token every authenticated call carries (§ F201). */
  token: string;
}

/** The account playing now. */
const KEY = "gtw_player";
/** § F201 — every account signed in on this device. */
const ACCOUNTS_KEY = "gtw_accounts";

/** Trivia's copies (see header). */
const LEGACY_TRIVIA = { id: "ppr_player_id", name: "ppr_player_name", token: "ppr_player_auth" };
/** Daily Challenges' pre-§ F117 per-tab slot. Only ever cleared now. */
const LEGACY_DAILY_CHALLENGES = "gtw_minigames_player";

/** Fired on every change, in THIS tab. `storage` covers the others. */
const EVENT = "gtw:player";

/**
 * A pre-§ F201 token was `HMAC(secret, player_id)` — 64 hex characters, handed
 * to anyone who tapped a name. The server no longer accepts it, so a device
 * still holding one is simply signed out rather than failing every call.
 */
const LEGACY_HMAC = /^[0-9a-f]{64}$/;

function read(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function valid(value: Partial<StoredPlayer> | null | undefined): StoredPlayer | null {
  if (!value?.id || !value?.name || !value?.token) return null;
  if (LEGACY_HMAC.test(value.token)) return null;
  return { id: value.id, name: value.name, token: value.token };
}

function parse(raw: string | null): StoredPlayer | null {
  if (!raw) return null;
  try {
    return valid(JSON.parse(raw) as Partial<StoredPlayer>);
  } catch {
    return null;
  }
}

/** Whoever is playing, or null. PURE — a read never writes. */
export function loadPlayer(): StoredPlayer | null {
  return parse(read(localStorage, KEY));
}

/** Every account signed in on this device, in the order they signed in. */
export function loadAccounts(): StoredPlayer[] {
  const raw = read(localStorage, ACCOUNTS_KEY);
  if (!raw) return [];
  try {
    const list = JSON.parse(raw) as Partial<StoredPlayer>[];
    if (!Array.isArray(list)) return [];
    return list.map(valid).filter((p): p is StoredPlayer => p !== null);
  } catch {
    return [];
  }
}

function writeAccounts(list: StoredPlayer[]): void {
  try {
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(list));
  } catch {
    /* private mode */
  }
}

function notify(): void {
  try {
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch {
    /* non-browser (tests) */
  }
}

/**
 * Drop anything a pre-§ F201 build left behind (an HMAC-token player, the
 * daily-challenges per-tab slot). Called once at start-up; idempotent.
 */
export function migrateLegacyPlayer(): void {
  try {
    const raw = read(localStorage, KEY);
    if (raw && !parse(raw)) {
      localStorage.removeItem(KEY);
      for (const k of Object.values(LEGACY_TRIVIA)) localStorage.removeItem(k);
    }
    sessionStorage.removeItem(LEGACY_DAILY_CHALLENGES);
  } catch {
    /* private mode */
  }
}

/**
 * Make `player` the one playing (adding it to this device's accounts), or
 * `null` to stop playing as anybody without signing anybody out.
 */
export function savePlayer(player: StoredPlayer | null): void {
  try {
    if (player) {
      localStorage.setItem(KEY, JSON.stringify(player));
      localStorage.setItem(LEGACY_TRIVIA.id, player.id);
      localStorage.setItem(LEGACY_TRIVIA.name, player.name);
      localStorage.setItem(LEGACY_TRIVIA.token, player.token);
      const list = loadAccounts();
      const at = list.findIndex((p) => p.id === player.id);
      if (at === -1) list.push(player);
      else list[at] = player;
      writeAccounts(list);
    } else {
      localStorage.removeItem(KEY);
      for (const k of Object.values(LEGACY_TRIVIA)) localStorage.removeItem(k);
    }
  } catch {
    /* private mode — the surface will ask again */
  }
  notify();
}

/**
 * Forget an account on this device. If it was the one playing, the next
 * signed-in account (if any) takes over, so a shared iPad does not fall back to
 * the sign-in screen while somebody else is still signed in on it.
 */
export function forgetAccount(id: string): void {
  const rest = loadAccounts().filter((p) => p.id !== id);
  writeAccounts(rest);
  if (loadPlayer()?.id === id) savePlayer(rest[0] ?? null);
  else notify();
}

/** Sign out: revoke the session on the server, then forget it here. */
export async function signOutPlayer(player: StoredPlayer): Promise<void> {
  try {
    await fetch("/api/account/logout", {
      method: "POST",
      headers: { Authorization: `Bearer ${player.token}` },
      keepalive: true,
    });
  } catch {
    /* forgetting it here is what matters to this device */
  }
  forgetAccount(player.id);
}

/** Subscribe to changes, in this tab and in the others. Returns an unsubscribe. */
export function subscribePlayer(onChange: () => void): () => void {
  const local = () => onChange();
  const cross = (event: StorageEvent) => {
    if (event.key === null || event.key === KEY || event.key === ACCOUNTS_KEY) onChange();
  };
  window.addEventListener(EVENT, local);
  window.addEventListener("storage", cross);
  return () => {
    window.removeEventListener(EVENT, local);
    window.removeEventListener("storage", cross);
  };
}

/**
 * § F138 — the app player a party room is allowed to remember. Every signed-in
 * player is an account now (§ F201 retired guests), so it is simply their id.
 */
export function appPlayerIdOf(player: StoredPlayer | null | undefined): string | null {
  return player ? player.id : null;
}

/** `Authorization` for the player playing now, or none. */
export function authHeaders(): Record<string, string> {
  const player = loadPlayer();
  return player ? { Authorization: `Bearer ${player.token}` } : {};
}
